/**
 * `#uc1FocusConstraints` — the five constraints of the focused unit, under the Overview's
 * focus tiles while the Priority chip is pressed (client round 3 Oct 2026).
 *
 * The Priority chip is the combined question, so a reader who focuses one watershed there
 * is asking what the combination is made of. The tiles answer with a count ("3 of 5
 * flagged"); this card is the count opened up: one row per constraint with its measured
 * value, the threshold it was read against and the verdict word.
 *
 * The rows are `buildUc1Rows` — the same builder the Key indicators scorecard and "Why
 * these areas" render — so the Overview cannot word the same number differently from the
 * two panels a scroll away. The chart above them is the same five rows as lengths, each on
 * its own domain (`scale="row"`: five units, five magnitudes, one shared axis would make
 * four of them slivers).
 *
 * A country (`admin0`) focus gets the same card reading the roll-up share per constraint,
 * which is what `buildUc1Rows` already serves at that level; those five bars share the one
 * 0–100 % scale, since they are five readings of the same quantity.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntelUnitDetail } from '../../lib/api';
import { DimensionBars, type DimRow } from './charts/DimensionBars';
import { CONSTRAINT_COLORS, UC1_DIMENSIONS } from './uc1Constants';
import { buildUc1Rows, rowLabel, type Uc1Values } from './uc1Rows';

export interface Uc1FocusConstraintsProps {
  /** `{uc}/units/{id}` for the focused unit. */
  detail: IntelUnitDetail;
}

/**
 * A driver number as a bar length: only an actual number is one. A reference may be an
 * absolute range (`[lo, hi]`), which has two edges and no one tick to draw, and a value
 * may be absent on a constraint the run could not score.
 */
function numOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function Uc1FocusConstraints({ detail }: Uc1FocusConstraintsProps) {
  const { t } = useTranslation();
  const values = detail.values as Uc1Values;
  const isCountry = detail.unit.level === 'admin0';
  const name = detail.unit.name;
  const title = t('intel.uc1.focusConstraints.title', { name });

  const rows = buildUc1Rows({
    t: (key, options) => t(key, options ?? {}),
    drivers: detail.drivers,
    values,
    country: detail.unit.country,
    level: detail.unit.level,
  });

  const byDim = new Map(detail.drivers.map((d) => [d.dimension, d]));
  const shares = values.rollup?.share_flagged ?? {};

  const bars: DimRow[] = UC1_DIMENSIONS.flatMap<DimRow>((dimension) => {
    const driver = byDim.get(dimension);
    if (!driver) return [];
    const label = rowLabel(t, dimension, isCountry ? null : driver.period);
    const flagged = isCountry
      ? typeof shares[dimension] === 'number' && (shares[dimension] as number) > 0
      : driver.flagged === true;
    const assessed = isCountry ? typeof shares[dimension] === 'number' : driver.flagged != null;
    const value = isCountry
      ? typeof shares[dimension] === 'number'
        ? (shares[dimension] as number) * 100
        : null
      : numOf(driver.value);
    return [
      {
        id: dimension,
        label,
        value,
        unit: isCountry ? '%' : (driver.unit ?? ''),
        reference: isCountry ? null : numOf(driver.reference),
        flagged,
        // Flagged carries the constraint's own family, every other state the chart's own
        // neutral — the colour says "this one fired", not "this one is soil".
        color: flagged ? CONSTRAINT_COLORS[dimension] : undefined,
        hatched: !assessed,
      },
    ];
  });

  return (
    <div
      id="uc1FocusConstraints"
      className="intel-card uc1-focus-constraints"
      data-testid="uc1-focus-constraints"
    >
      <h6 className="intel-card-title">{title}</h6>
      {bars.length ? (
        <DimensionBars
          rows={bars}
          ariaLabel={title}
          scale={isCountry ? 'shared' : 'row'}
          max={isCountry ? 100 : undefined}
          referenceLabel={t('intel.chart.reference')}
          refCaption={t('intel.chart.ref')}
        />
      ) : null}
      <ul className="uc1-scorecard">
        {rows.map((row) => (
          <li
            key={row.dimension}
            data-testid={`uc1-focus-constraint-${row.dimension}`}
            className={`uc1-row ${row.status}`}
            style={{ '--uc1-row-color': CONSTRAINT_COLORS[row.dimension] } as CSSProperties}
          >
            <span className="uc1-dot" aria-hidden="true" />
            <span className="uc1-row-text">
              {row.text} <span className="uc1-row-status">{row.iconLabel}</span>
              {row.tail ? <> {row.tail}</> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Uc1FocusConstraints;
